import {
  adminDownTransVars,
  defaultDownTransVars,
  deleteStepDownTransformers,
  ecopetrolDownTransVars,
  kocDownTransVars,
  restoreStepDownTransformers
} from "@/models/equipment/step_down_transformer/predicates";
import type { Step_Down_Transformers } from "gql/api";
import {
  useStepDownTransformersQuery,
  useInsertDownTransMutation,
  useUpdateDownTransByPkMutation,
  useDeleteDownTransMutation,
  useRestoreDownTransMutation
} from "gql/api";
import { v4 } from "uuid";
import { computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { sdtTableColumns } from "./sdtTableColumns";
import useUserInfo from "@/composables/auth/useUserInfo";

export function useStepDownTransformers(
  isAdmin: boolean
): UseEquipment<Step_Down_Transformers> {
  const baseEquipment = useEquipment<Step_Down_Transformers>(isAdmin);
  const title = "Step Down Transformers";
  const user = useUserInfo();

  const getVariables = () => {
    if (user.is_koc_customer) return kocDownTransVars;
    if (user.is_ecopetrol_customer) return ecopetrolDownTransVars;
    if (user.is_admin) return adminDownTransVars;
    return defaultDownTransVars;
  };

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useStepDownTransformersQuery({
    variables: getVariables()
  });

  const allRows = computed(
    () =>
      data.value?.step_down_transformers as Step_Down_Transformers[] | undefined
  );

  const hashOrder: (keyof Step_Down_Transformers)[] = [
    "manufacturer",
    "part_number",
    "transformer_type",
    "primary_voltage_delta",
    "primary_voltage_wye",
    "min_secondary_voltage",
    "max_secondary_voltage",
    "enclosure",
    "kva"
  ];

  const { defineVisibleColumns, defineFilterData } = baseEquipment;

  const { defineUpsert, defineRestore, defineDelete } =
    baseEquipment.defineMutationHandlers(refetch, hashOrder);

  const { executeMutation: executeInsertDownTrans } =
    useInsertDownTransMutation();
  const { executeMutation: executeUpdateDownTrans } =
    useUpdateDownTransByPkMutation();
  const { executeMutation: executeDeleteDownTrans } =
    useDeleteDownTransMutation();
  const { executeMutation: executeRestoreDownTrans } =
    useRestoreDownTransMutation();

  const insertDownTrans = (
    downtrans: Omit<Step_Down_Transformers, "sizing_import" | "user">
  ) => executeInsertDownTrans({ object: downtrans });

  const updateDownTrans = (id: string, downtrans: Step_Down_Transformers) =>
    executeUpdateDownTrans({ id, object: downtrans });

  const deleteDownTrans = (ids: string[]) =>
    executeDeleteDownTrans({ where: deleteStepDownTransformers(ids) });

  const restoreDownTrans = (ids: string[]) =>
    executeRestoreDownTrans({ where: restoreStepDownTransformers(ids) });

  baseEquipment.defineEmptyRow(() => ({
    __typename: "step_down_transformers",
    id: v4(),
    created_at: "now()",
    updated_at: "now()",
    deleted_at: null,
    imported_sizing_id: null,
    manufacturer: "",
    part_number: "",
    transformer_type: "",
    enclosure: "",
    primary_voltage_wye: null,
    primary_voltage_delta: null,
    min_secondary_voltage: null,
    max_secondary_voltage: null,
    weight_pounds: null,
    kva: null
  }));

  return {
    searchPlaceholder: "Search Step Down Transformers...",
    searchToolTip: `Add quotes around search terms for a more exact result. Ex: "38"`,
    columns: defineVisibleColumns(sdtTableColumns),
    handleUpsert: defineUpsert(insertDownTrans, updateDownTrans),
    handleDelete: defineDelete(deleteDownTrans),
    handleRestore: defineRestore(restoreDownTrans),
    totalCount: computed(
      () => data.value?.step_down_transformers_aggregate.aggregate?.count
    ),
    title,
    allRows,
    isAdmin,
    refetch,
    isStale,
    ...baseEquipment,
    ...defineFilterData({
      columns: sdtTableColumns,
      globalSearchFields: ["kva", "transformer_type", "enclosure"],
      allRows,
      title
    })
  };
}
